import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Paperclip } from "lucide-react";
import logoPng64 from "@/assets/logo-64.png";

interface Message {
  role: "user" | "assistant";
  content: string;
}

// Colors from your chatbot.css
const COLORS = {
  accent: "#3B82F6",    // Blue (User bubble & Button)
  bg: "#0f1419",        // Darker Grey (Container BG)
  text: "#F9FAFB",      // White Text
  border: "#4B5563",    // Border Grey
  botBubble: "#1F2937", // Bot Bubble Grey
  userBubble: "#3B82F6" // User Bubble Blue
};

const initialMessages: Message[] = [
  { role: "assistant", content: "Hi there! I am ClearMakes. Feel free to ask me any questions about this page or our platform!" }
];

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showBubble, setShowBubble] = useState(true);
  const [isShrunk, setIsShrunk] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-hide bubble + shrink icon to dot after 15 seconds
  useEffect(() => {
    const bubbleTimer = setTimeout(() => setShowBubble(false), 15000);
    const shrinkTimer = setTimeout(() => setIsShrunk(true), 15000);
    return () => { clearTimeout(bubbleTimer); clearTimeout(shrinkTimer); };
  }, []);

  // Scroll to bottom logic
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Lock body scroll on mobile when open (uses matchMedia to avoid forced reflows)
  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    if (isMobile) {
      document.body.style.overflow = isOpen ? 'hidden' : '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    
    // 1. Update UI immediately
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch('/api/chatbot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          page: 'home' 
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      const botReply = data.reply || "I'm sorry, I couldn't get a response. Please try again.";
      
      setMessages(prev => [...prev, { role: "assistant", content: botReply }]);
    }finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* "Feel free to ask" Bubble */}
      <AnimatePresence>
        {showBubble && !isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="fixed bottom-24 right-5 z-40 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium hidden md:block"
            style={{ 
              backgroundColor: COLORS.bg, 
              color: COLORS.text, 
              borderColor: COLORS.border 
            }}
          >
            <p>Feel free to ask a question!</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Launcher Button — round circle with logo, shrinks to dot after 15s */}
      <motion.button
        onClick={() => {
          setIsOpen(!isOpen);
          setShowBubble(false);
          setIsShrunk(false);
        }}
        className="fixed bottom-5 right-5 z-50 flex items-center justify-center rounded-full shadow-xl"
        style={{
          backgroundColor: '#1a1d24',
          border: '1px solid rgba(255,255,255,0.08)',
          width: isOpen ? 56 : isShrunk ? 40 : 56,
          height: isOpen ? 56 : isShrunk ? 40 : 56,
          transition: 'width 0.3s ease, height 0.3s ease, box-shadow 0.2s ease',
          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
        }}
        whileHover={{ scale: isShrunk ? 1.15 : 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
            >
              <X size={24} className="text-white" />
            </motion.div>
          ) : isShrunk ? (
            <motion.div
              key="small-logo"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="flex items-center justify-center"
            >
              <img src={logoPng64} alt="ClearMakes" className="w-7 h-7 object-contain rounded-md" />
            </motion.div>
          ) : (
            <motion.div
              key="logo"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="flex items-center justify-center"
            >
              <img src={logoPng64} alt="ClearMakes" className="w-9 h-9 object-contain rounded-md" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            // FIXED: Added 'md:left-auto' to prevent it from sticking to the left on Desktop
            className="fixed z-[60] flex flex-col overflow-hidden shadow-2xl
                       md:bottom-24 md:right-5 md:left-auto md:w-[350px] md:h-[500px] md:rounded-xl md:border
                       bottom-0 left-0 right-0 w-full h-[100dvh] rounded-none border-none" 
            style={{ 
              backgroundColor: COLORS.bg, 
              borderColor: COLORS.border 
            }}
          >
            {/* Header */}
            <div 
              className="p-4 flex items-center justify-between border-b"
              style={{ backgroundColor: COLORS.botBubble, borderColor: COLORS.border }}
            >
              <div className="flex items-center gap-3">
                {/* Updated: Blue background for the header icon to match the launcher */}
                <div
                  className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center"
                  style={{ backgroundColor: '#1a1d24' }}
                >
                  <img src={logoPng64} alt="ClearMakes" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm" style={{ color: COLORS.text }}>ClearMakes</h3>
                  {/* Removed "Online" text */}
                </div>
              </div>
              
              {/* Mobile Close Button */}
              <button 
                onClick={() => setIsOpen(false)}
                className="md:hidden p-2 text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" id="chat-widget-history">
              {messages.map((message, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div 
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl ${
                      message.role === "user" ? "rounded-br-sm" : "rounded-bl-sm"
                    }`}
                    style={{ 
                      backgroundColor: message.role === "user" ? COLORS.userBubble : COLORS.botBubble,
                      color: message.role === "user" ? "#FFF" : COLORS.text 
                    }}
                  >
                    {message.content}
                  </div>
                </motion.div>
              ))}
              
              {/* Typing Indicator */}
              {isLoading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                  <div 
                    className="px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1 items-center"
                    style={{ backgroundColor: COLORS.botBubble }}
                  >
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-gray-400"
                        animate={{ y: [0, -4, 0] }}
                        transition={{ duration: 0.6, delay: i * 0.2, repeat: Infinity }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Form - Google AI Style */}
            <form 
              onSubmit={handleSubmit} 
              className="p-3 md:p-4 flex items-end gap-2 md:gap-3"
              style={{ 
                background: `linear-gradient(180deg, transparent 0%, ${COLORS.bg} 30%)`,
                borderTop: 'none'
              }}
            >
              {/* Attachment Button */}
              <button
                type="button"
                className="w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
                onClick={() => {/* Attachment handler */}}
              >
                <Paperclip size={18} />
              </button>
              
              {/* Input Wrapper with animated border on loading */}
              <div 
                className={`flex-1 rounded-full transition-all ${
                  isLoading 
                    ? 'animate-gradient-border' 
                    : 'border border-white/20 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20'
                }`}
                style={{ 
                  backgroundColor: '#111827',
                  ...(isLoading && {
                    background: 'linear-gradient(#111827, #111827) padding-box, linear-gradient(90deg, #3B82F6, #8B5CF6, #EC4899, #3B82F6) border-box',
                    border: '1px solid transparent',
                    backgroundSize: '100% 100%, 300% 100%',
                    animation: 'gradient-x 2s ease infinite'
                  })
                }}
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Message ClearMakes..."
                  className="w-full bg-transparent px-4 py-3 text-sm md:text-base focus:outline-none"
                  style={{ color: COLORS.text }}
                />
              </div>
              
              {/* Send Button */}
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-all text-white shadow-lg hover:scale-105 flex-shrink-0"
                style={{ 
                  background: 'linear-gradient(135deg, #3B82F6 0%, #6366f1 100%)',
                  boxShadow: '0 2px 10px rgba(59, 130, 246, 0.3)'
                }}
              >
                <Send size={18} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* CSS for gradient animation */}
      <style>{`
        @keyframes gradient-x {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient-border {
          animation: gradient-x 2s ease infinite;
        }
      `}</style>
    </>
  );
};